import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Text, Tip } from 'grommet';
import PropTypes from 'prop-types';
import { DocumentDownload, Link, View } from 'grommet-icons';
// Relative rather than '@enrich/shared-lib': this file lives inside the package
// and the barrel re-exports it, so importing the barrel here is a cycle.
import { openInNewTab } from '../../helpers/NewTabOpen';
import { ResumeContentViewer } from '../../components/ResumeContentViewer';

const LINK_DOCUMENT_TYPES = ['Resume'];

const documentTitle = (document) =>
    document?.attributes?.document_type?.data?.attributes?.title;

export const ContactDocuments = ({ documents, contact }) => {
    const [highlightedDocument, setHighlightedDocument] = useState({});
    const resumeContentRef = useRef(null);
    const isMounted = useRef(true);
    const [downloadBusy, setDownloadBusy] = useState(false);
    const [downloadError, setDownloadError] = useState('');

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleDownload = async () => {
        setDownloadBusy(true);
        setDownloadError('');
        try {
            const printTarget =
                resumeContentRef.current?.querySelector('.ProseMirror') ||
                resumeContentRef.current;
            if (!printTarget) throw new Error('Nothing to print');
            // Loaded on demand: html2canvas + jspdf are ~1MB, and this page
            // renders for every contact whether or not anyone downloads.
            const { downloadResumePdf } =
                await import('../../helpers/downloadResumePdf');
            await downloadResumePdf(
                printTarget,
                `${contact?.attributes?.firstName || 'resume'}.pdf`,
            );
        } catch (error) {
            console.log(error);
            if (isMounted.current) {
                setDownloadError('Could not build the PDF. Please try again.');
            }
        }
        if (isMounted.current) setDownloadBusy(false);
    };

    const handleClick = (document) => {
        setDownloadError('');

        if (document.id === highlightedDocument.id) {
            setHighlightedDocument({});
            return;
        }

        if (document.attributes.link) {
            setHighlightedDocument({});
            openInNewTab(document.attributes.link);
            return;
        }

        if (document.attributes.content) {
            setHighlightedDocument(document);
            return;
        }
    };

    return (
        <Box gap="small">
            <Box direction="row-responsive" gap="small">
                {documents.map((document) => {
                    if (LINK_DOCUMENT_TYPES.includes(documentTitle(document))) {
                        return (
                            <Tip
                                content={
                                    document.attributes.link ||
                                    'No link available'
                                }
                                key={document.id}>
                                <Box>
                                    <DocumentBox
                                        document={document}
                                        highlightedDocumentId={
                                            highlightedDocument.id
                                        }
                                        handleClick={handleClick}
                                    />
                                </Box>
                            </Tip>
                        );
                    } else {
                        return (
                            <DocumentBox
                                document={document}
                                highlightedDocumentId={highlightedDocument.id}
                                handleClick={handleClick}
                                key={document.id}
                            />
                        );
                    }
                })}
            </Box>
            {highlightedDocument?.attributes?.content && (
                <Box width="100%">
                    <Box ref={resumeContentRef}>
                        <ResumeContentViewer
                            content={highlightedDocument.attributes.content}
                        />
                    </Box>
                    {LINK_DOCUMENT_TYPES.includes(
                        documentTitle(highlightedDocument),
                    ) && (
                        <Button
                            alignSelf="end"
                            size="small"
                            label="Download PDF"
                            icon={<DocumentDownload />}
                            busy={downloadBusy}
                            onClick={handleDownload}
                        />
                    )}
                    {downloadError && (
                        <Text
                            size="small"
                            color="status-critical"
                            alignSelf="end"
                            role="alert">
                            {downloadError}
                        </Text>
                    )}
                </Box>
            )}
        </Box>
    );
};

const DocumentBox = ({ document, handleClick, highlightedDocumentId }) => {
    const isHighlightedDocument = highlightedDocumentId === document.id;
    const documentType = document.attributes.document_type?.data;

    return (
        <Box
            background={isHighlightedDocument ? 'enrich-green-light' : 'white'}
            border={{ color: 'enrich-green', size: '2px' }}
            cursor="pointer"
            direction="row"
            gap="small"
            hoverIndicator="background"
            onClick={() => handleClick(document)}
            round="6px"
            pad="small"
            width="small">
            {documentType?.attributes.title || 'Missing document type'}
            {document.attributes.link && <Link color="black" />}
            {document.attributes.content && <View color="black" />}
        </Box>
    );
};

ContactDocuments.propTypes = {
    documents: PropTypes.array,
    contact: PropTypes.object,
};

DocumentBox.propTypes = {
    document: PropTypes.object,
    handleClick: PropTypes.func,
    highlightedDocumentId: PropTypes.string,
};
